/**
 * Solo mode's progress and access (ADR 0020). Solo play runs in this browser with bot teammates, so
 * its progress lives here too (in localStorage): the missions won and the best stars for each. Kept
 * per profile: the browser's guest, and each person who signs in here, whose copy also syncs with
 * the api (ADR 0029, `syncProgress`).
 *
 * `soloMissions` is the one place that decides what a solo player may play. Today that's the free
 * campaign as they win their way through it, extras once their mission is won (ADR 0019), each
 * track's missions in turn (ADR 0027), and the missions of the packs they unlock (the Obean Pass's,
 * ADR 0022: solo itself stays free).
 */
import type { Progress } from "@obean/game-contract";
import { addsTo, cleanProgress, mergeProgress } from "@obean/game-contract";

/** Missions won (with the best stars) and the run's best: the shared record (ADR 0029). */
export type SoloProgress = Progress;

export interface MissionInfo {
  id: string;
  name: string;
  pack?: string | undefined;
  tutorial?: boolean | undefined;
  opensAfter?: string | undefined;
  /** Outside the line and open from the start (ADR 0019), like a battle mode. */
  openFromStart?: boolean | undefined;
  /** Its own line (ADR 0027), e.g. a quiz category: the first open, each next after a win. */
  track?: string | undefined;
}

export interface SoloMission extends MissionInfo {
  /** Whether it can be played now, and if not, why. */
  open: boolean;
  why: "" | "win_previous" | "win_after" | "pack";
  /** Best stars so far (undefined: never won). */
  stars: number | undefined;
}

/** The guest's copy, or a signed-in person's (`profile`, their id). */
const KEY = (gameId: string, profile: string | null) =>
  profile === null ? `obean.solo.${gameId}` : `obean.solo.${profile}.${gameId}`;

export const emptyProgress = (): SoloProgress => ({ won: {}, bestRun: 0 });

/** This browser's solo progress for a game and profile; empty if there's none, or no storage. */
export function loadProgress(
  gameId: string,
  storage: Pick<Storage, "getItem"> | null = safeStorage(),
  profile: string | null = null,
): SoloProgress {
  try {
    const raw = storage?.getItem(KEY(gameId, profile));
    return raw ? cleanProgress(JSON.parse(raw)) : emptyProgress();
  } catch {
    return emptyProgress();
  }
}

export function saveProgress(
  gameId: string,
  progress: SoloProgress,
  storage: Pick<Storage, "setItem"> | null = safeStorage(),
  profile: string | null = null,
): void {
  try {
    storage?.setItem(KEY(gameId, profile), JSON.stringify(progress));
  } catch {
    // Private windows and full storage: progress just isn't kept.
  }
}

/** The api calls a sync needs (net/api.ts), passed in so tests can stand in for them. */
export interface ProgressApi {
  progress: (gameId: string) => Promise<{ progress: SoloProgress }>;
  saveProgress: (gameId: string, progress: SoloProgress) => Promise<{ progress: SoloProgress }>;
}

/**
 * A signed-in person's progress, synced (ADR 0029): this browser's copy for them, plus the guest's
 * if the browser is their own, merged with the api's; whatever the api lacked is sent to it. The
 * merged record is kept here and returned. Offline, the local copy is the answer.
 */
export async function syncProgress(
  gameId: string,
  profile: string,
  ownBrowser: boolean,
  remote: ProgressApi,
  storage: Pick<Storage, "getItem" | "setItem"> | null = safeStorage(),
): Promise<SoloProgress> {
  let local = loadProgress(gameId, storage, profile);
  if (ownBrowser) local = mergeProgress(local, loadProgress(gameId, storage, null));
  let merged = local;
  try {
    const server = (await remote.progress(gameId)).progress;
    merged = mergeProgress(local, server);
    if (addsTo(server, merged)) merged = (await remote.saveProgress(gameId, merged)).progress;
  } catch {
    // Offline or signed out meanwhile: keep what's here; the next sync catches up.
  }
  saveProgress(gameId, merged, storage, profile);
  return merged;
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** Progress after a finished solo match: a win records the mission (best stars kept); a run its reach. */
export function recordResult(
  progress: SoloProgress,
  missionId: string,
  outcome: { result: "win" | "loss"; stats: Record<string, number> },
): SoloProgress {
  const next: SoloProgress = { won: { ...progress.won }, bestRun: progress.bestRun };
  const cleared = outcome.stats["missions"];
  if (cleared !== undefined) next.bestRun = Math.max(next.bestRun, cleared);
  if (outcome.result === "win")
    next.won[missionId] = Math.max(next.won[missionId] ?? 0, outcome.stats["stars"] ?? 0);
  return next;
}

/**
 * The game's missions for a solo player, in the room's order: the campaign line (each opens when
 * the one before is won), the extras whose mission is won, then pack missions (open if owned).
 */
export function soloMissions(
  missions: readonly MissionInfo[],
  progress: SoloProgress,
  ownedPacks: readonly string[],
): SoloMission[] {
  const won = (id: string) => progress.won[id] !== undefined;
  const inLine = (m: MissionInfo) =>
    !m.pack && !m.opensAfter && !m.openFromStart && m.track === undefined;
  const line = missions.filter(inLine);
  const out: SoloMission[] = line.map((m, i) => {
    const before = line[i - 1];
    const open = i === 0 || (before !== undefined && won(before.id));
    return { ...m, open, why: open ? "" : "win_previous", stars: progress.won[m.id] };
  });
  // Tracks (ADR 0027), in manifest order: each its own line.
  missions.forEach((m, i) => {
    if (m.track === undefined || m.pack) return;
    const before = missions.slice(0, i).findLast((x) => x.track === m.track);
    const open = before === undefined || won(before.id);
    out.push({ ...m, open, why: open ? "" : "win_previous", stars: progress.won[m.id] });
  });
  for (const m of missions.filter(
    (x) => !x.pack && x.track === undefined && (x.opensAfter || x.openFromStart),
  )) {
    const open = m.openFromStart === true || won(m.opensAfter ?? "");
    out.push({ ...m, open, why: open ? "" : "win_after", stars: progress.won[m.id] });
  }
  for (const m of missions.filter((x) => x.pack)) {
    const open = ownedPacks.includes(m.pack ?? "");
    out.push({ ...m, open, why: open ? "" : "pack", stars: progress.won[m.id] });
  }
  return out;
}

/** The next open mission after `id` in the solo list, if any (the "Next mission" button). */
export function nextMission(list: readonly SoloMission[], id: string): SoloMission | undefined {
  const i = list.findIndex((m) => m.id === id);
  const track = list[i]?.track;
  // In a track (ADR 0027), the next is the track's own next; otherwise the line's.
  if (track !== undefined) return list.slice(i + 1).find((m) => m.track === track && m.open);
  return list
    .slice(i + 1)
    .find((m) => m.open && !m.opensAfter && !m.openFromStart && m.track === undefined);
}
